import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Select from 'react-select';
import * as licenceActions from '../../../actions/licenceActions';
import { licenceManagerAccess } from '../../../utils/accessLevelCheck';
import SecondaryMenu from '../../Global/Menus/SecondaryMenu';
import AccountMenu from '../AccountMenu';
import PageContainer from '../../Global/Containers/PageContainer';
import MaxWidthContainer from '../../Global/Containers/MaxWidthContainer';
import LicenceeTable from './LicenceeTable';
import Button from '../../Global/Buttons/Button';
import SelectContainer from '../../Global/Forms/SelectContainer';
import IconWithText from '../../Global/Icons/IconWithText';
import AllocateLicenceModal from './AllocateLicenceModal';
import AllocateLicenceForm from './AllocateLicenceForm';
import RemoveLicenceModal from './RemoveLicenceModal';
import UpsellModal from '../../Global/Modals/UpsellModal';
import Pager from '../../Global/Pager/Pager';

class LicenceManagerPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      loadingPager: false,
      loadingLicencees: true,
      defaultAllocateLicenceType: `premium`,
      allocateLicenceModalOpen: false,
      allocateLicenceModalLoading: false,
      removeLicenceModalOpen: false,
      removeLicenceModalLoading: false,
      licenceeToRemove: {},
      sortByOption: `first-name`,
      upsellModalOpen: false,
      canUserAccess: false,
      editingLicencees: [],
    };
  }

  componentWillReceiveProps(nextProps) {
    // If the user isn't logged in then hide the loader icon
    if (this.props.loggedInUser.notLoggedIn !== nextProps.loggedInUser.notLoggedIn) {
      this.setState({ loadingLicencees: false });
    } else {
      this.setState({ canUserAccess: licenceManagerAccess(nextProps.loggedInUser.computed_access) });
    }

    // If we've loaded more licencees
    if (this.props.licences.allocated !== nextProps.licences.allocated) {
      this.setState({
        loadingPager: false,
        loadingLicencees: false,
        allocateLicenceModalOpen: false,
        allocateLicenceModalLoading: false,
        removeLicenceModalOpen: false,
        removeLicenceModalLoading: false,
      });
    }
  }

  removeFromEditingArray = (licenceIdToRemove) => {
    const removedFromArray = this.state.editingLicencees.filter(licenceId => licenceId !== licenceIdToRemove);
    this.setState({ editingLicencees: removedFromArray });
  }

  handleLicenceChange = (licenceeToChange, newLicenceType) => {
    const addedToArray = [licenceeToChange.id, ...this.state.editingLicencees];
    this.setState({ editingLicencees: addedToArray });
    this.props.actions.editLicence(licenceeToChange.id, newLicenceType)
    // If a licence has been successfully edited
    .then(() => {
      this.removeFromEditingArray(licenceeToChange.id);
    })
    .catch(() => {
      this.removeFromEditingArray(licenceeToChange.id);
    });
  }

  handleAllocateLicenceCTAClick = (e) => {
    e.preventDefault();
    if (this.props.loggedInUser.computed_access) {
      if (this.state.canUserAccess === true) {
        this.setState({ allocateLicenceModalOpen: true });
      } else {
        // Logged in but don't have access to the feature
        this.setState({ upsellModalOpen: true });
      }
    } else {
      // Logged out users
      this.setState({ upsellModalOpen: true });
    }
  }

  handleAllocateLicenceSubmit = (values) => {
    this.setState({ allocateLicenceModalLoading: true });
    this.props.actions.allocateLicence(values.emailAddress, values.licenceType)
    .catch(() => {
      this.setState({ allocateLicenceModalLoading: false });
    });
  }

  handleDeleteClick = (licenceeToRemove) => {
    this.setState({ licenceeToRemove, removeLicenceModalOpen: true });
  }

  closeModal = (modal) => {
    this.setState({ [modal]: false });
  }

  removeLicence = (e) => {
    e.preventDefault();
    this.setState({ removeLicenceModalLoading: true });
    this.props.actions.removeLicence(this.state.licenceeToRemove.attributes[`user-id`], this.state.licenceeToRemove.id)
    .catch(() => {
      this.setState({ removeLicenceModalOpen: false, removeLicenceModalLoading: false });
    });
  }

  handleSortByChange = (selectedOption) => {
    this.setState({ sortByOption: selectedOption.value, loadingLicencees: true });
    this.props.actions.getAllocatedLicences(this.state.currentPage, selectedOption.value);
  }

  handlePrevClick = () => {
    this.setState({ currentPage: this.state.currentPage - 1 }, () => {
      this.handlePagerClick();
    });
  }

  handleNextClick = () => {
    this.setState({ currentPage: this.state.currentPage + 1 }, () => {
      this.handlePagerClick();
    });
  }

  handlePagerClick = () => {
    this.setState({ loadingPager: true });
    this.props.actions.getAllocatedLicences(this.state.currentPage, this.state.sortByOption);
  }

  render() {
    const { loggedInUser, licences } = this.props;
    const { loadingPager, loadingLicencees, defaultAllocateLicenceType, allocateLicenceModalOpen, allocateLicenceModalLoading, removeLicenceModalOpen, removeLicenceModalLoading, licenceeToRemove, sortByOption, upsellModalOpen, canUserAccess, editingLicencees } = this.state;

    return (
      <div id="licence-manager-page">
        <PageContainer classes="bg-white">
          <SecondaryMenu>
            <AccountMenu active="licence-manager" billingEnabled={loggedInUser.billing_enabled} />
          </SecondaryMenu>

          <MaxWidthContainer>
            <h1 className="like-h2 m-t-5 m-b-1">Licence Manager</h1>
            <p className="licence-manager-page__intro-paragraph like-h5 color-grey max-width-paragraph">
              Here you can allocate and manage your licences. If you require additional licences then please contact us at <a href="mailto:help@thecoachingmanual.com?Subject=Additional licences" className="link">help@thecoachingmanual.com</a>.
            </p>

            <div className="licence-manager-table-container">
              <div className="licence-manager-table-actions m-b-3 d-flex align-items-center justify-content-space-between">
                <Button onClick={this.handleAllocateLicenceCTAClick} classes="licence-manger-table-actions__allocate-btn align-self-start m-b-5"><IconWithText size="m" name="person-add" fill="white">Allocate licence</IconWithText></Button>

                {canUserAccess &&
                  <div className="align-self-end">
                    <SelectContainer label="Sort by:">
                      <Select
                        name="sort-by"
                        value={sortByOption}
                        onChange={this.handleSortByChange}
                        clearable={false}
                        searchable={false}
                        options={[
                          { value: `first-name`, label: `First name` },
                          { value: `last-name`, label: `Last name` },
                          { value: `email-address`, label: `Email address` },
                          { value: `licence-type`, label: `Licence type` },
                          { value: `last-active`, label: `Last active` },
                        ]}
                      />
                    </SelectContainer>
                  </div>
                }
              </div>

              <LicenceeTable
                loading={loadingLicencees}
                licences={licences}
                onLicenceChange={this.handleLicenceChange}
                onDeleteClick={this.handleDeleteClick}
                editingLicencees={editingLicencees}
              />

              {licences.links &&
                <Pager
                  loading={loadingPager}
                  showPrevButton={`prev` in licences.links}
                  showNextButton={`next` in licences.links}
                  onPrevClick={this.handlePrevClick}
                  onNextClick={this.handleNextClick}
                />
              }
            </div>
          </MaxWidthContainer>

          <AllocateLicenceModal
            modalIsOpen={allocateLicenceModalOpen}
            onCloseClick={() => this.closeModal(`allocateLicenceModalOpen`)}
          >
            <AllocateLicenceForm
              licenceType={defaultAllocateLicenceType}
              onCloseClick={() => this.closeModal(`allocateLicenceModalOpen`)}
              onSubmit={this.handleAllocateLicenceSubmit}
              loading={allocateLicenceModalLoading}
            />
          </AllocateLicenceModal>

          <RemoveLicenceModal
            modalIsOpen={removeLicenceModalOpen}
            loading={removeLicenceModalLoading}
            onConfirmClick={this.removeLicence}
            onCloseClick={() => this.closeModal(`removeLicenceModalOpen`)}
            licencee={licenceeToRemove}
          />

          <UpsellModal
            loggedInUser={loggedInUser}
            modalIsOpen={upsellModalOpen}
            upsellFeature="licences"
            onCloseClick={() => this.closeModal(`upsellModalOpen`)}
          />
        </PageContainer>
      </div>
    );
  }
}

LicenceManagerPage.propTypes = {
  loggedInUser: PropTypes.object.isRequired,
  licences: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    loggedInUser: state.loggedInUser,
    licences: state.licences,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign({}, licenceActions), dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LicenceManagerPage);



// WEBPACK FOOTER //
// ./library/app/components/Account/LicenceManager/LicenceManagerPage.js